import {
  safeGoogleImageUrl,
  safeGoogleSourceUrl,
  safePublicWebUrl,
} from '@voll-intelligence/domain';
import { z } from 'zod';

// The same rules the server sanitized with (`@voll-intelligence/domain`): the
// browser re-validates the wire, and a divergent copy failed the whole parse.
const SafeWebUrlSchema = z
  .string()
  .refine((value) => safePublicWebUrl(value) !== undefined, 'unsafe web URL');
const GoogleMapsUrlSchema = z
  .string()
  .refine((value) => safeGoogleSourceUrl(value) !== undefined, 'invalid Google Maps URL');
export const GooglePhotoUriSchema = z
  .string()
  .refine((value) => safeGoogleImageUrl(value) !== undefined, 'invalid Google photo URL');

const AttributionSchema = z.object({
  displayName: z.string().min(1).optional(),
  uri: GoogleMapsUrlSchema.optional(),
  photoUri: GooglePhotoUriSchema.optional(),
});

const PlacePhotoSchema = z.object({
  name: z.string().min(1),
  widthPx: z.number().int().positive().optional(),
  heightPx: z.number().int().positive().optional(),
  authorAttributions: z.array(AttributionSchema).default([]),
  googleMapsUri: GoogleMapsUrlSchema.optional(),
});

const PlaceReviewSchema = z.object({
  text: z.string().min(1),
  rating: z.number().min(0).max(5).optional(),
  relativeTime: z.string().min(1).optional(),
  authorAttribution: AttributionSchema.optional(),
  googleMapsUri: GoogleMapsUrlSchema.optional(),
});

/** Live Google content returned by the authenticated host port; never part of the persisted wire. */
export const PlaceDetailsSchema = z.object({
  placeId: z.string().min(1),
  name: z.string().min(1),
  address: z.string().min(1).optional(),
  location: z.object({ latitude: z.number(), longitude: z.number() }),
  types: z.array(z.string()),
  rating: z.number().min(0).max(5).optional(),
  userRatingCount: z.number().int().nonnegative().optional(),
  priceLevel: z.number().int().min(1).max(4).optional(),
  openNow: z.boolean().optional(),
  weekdayHours: z.array(z.string().min(1)).optional(),
  editorialSummary: z.string().min(1).optional(),
  reviewSummary: z.string().min(1).optional(),
  primaryTypeDisplayName: z.string().min(1).optional(),
  googleMapsUri: GoogleMapsUrlSchema.optional(),
  websiteUri: SafeWebUrlSchema.optional(),
  phoneNumber: z.string().min(1).optional(),
  photos: z.array(PlacePhotoSchema).default([]),
  reviews: z.array(PlaceReviewSchema).default([]),
});

export type PlaceDetails = z.infer<typeof PlaceDetailsSchema>;
export type PlaceDetailLevel = 'summary' | 'full';

export interface PlacesPort {
  getPlace(placeId: string, detail: PlaceDetailLevel, languageCode: string): Promise<PlaceDetails>;
  getPhoto(name: string): Promise<string | null>;
}
